import { useState, useEffect } from "react";
import { applyCandidateStatus, getCandidatesStatuses, sendOffer } from "../../services/candidateApi";
import { useAlertContext } from "../../context/AlertContext";
import OfferModal from "./OfferModal";
import CandidateStatusTree from "./CandidateStatusTree";
import { buildOfferLetterText } from "../../utils/interviewInvite";
import { allowedStatusTransitions, DATE_STATUS_VALUES, statusLabel, statusNextAction, statusStage, statusState } from "../../utils/candidateStatuses";

export default function CandidateStatusControls({ candidate, onChange, showOffer = true, onHire }) {
    const [loading, setLoading] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(null);
    const { showAlert } = useAlertContext();
    const [statuses, setStatuses] = useState([]);
    const [offerOpen, setOfferOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [pendingDate, setPendingDate] = useState("");
    const allowed = allowedStatusTransitions(currentStatus, statuses);

    useEffect(() => {
        if (!candidate) return;
        setCurrentStatus(candidate?.status ?? candidate?.status_code ?? candidate?.status_name ?? null);
    }, [candidate?.id, candidate?.status, candidate?.status_code, candidate?.status_name, candidate]);

    useEffect(() => {
        const loadStatuses = async () => {
            try {
                const list = await getCandidatesStatuses();
                setStatuses(list);
            } catch (e) {
                setStatuses([]);
            }
        };
        loadStatuses();
    }, []);

    const handleStatusChange = async (newStatus, statusDate = null) => {
        if (newStatus === "в штате") {
            if (onHire) onHire();
            else showAlert("Для добавления в сотрудники укажите дату выхода на работу", "warning");
            return;
        }
        const catalogStatus = statuses.find((entry) => entry.code === newStatus);
        if ((catalogStatus?.requiresDate ?? DATE_STATUS_VALUES.has(newStatus)) && !statusDate) {
            setPendingStatus(newStatus);
            setPendingDate("");
            return;
        }
        try {
            setLoading(true);
            await applyCandidateStatus(candidate.id, newStatus, statusDate);
            setCurrentStatus(newStatus);
            setPendingStatus(null);
            showAlert("Статус обновлен", "success");
            onChange?.(newStatus, statusDate);
        } catch (e) {
            const message = e?.message || "Не удалось обновить статус";
            showAlert(`Ошибка: ${message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleOfferSubmit = async (offer) => {
        try {
            setLoading(true);
            await sendOffer(candidate.id, offer);
            setCurrentStatus("оффер: отправлен");
            showAlert("Оффер отправлен", "success");
            setOfferOpen(false);
            onChange?.("оффер: отправлен", null);
        } catch (e) {
            showAlert(`Ошибка отправки оффера: ${e.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    if (!candidate) {
        return <div className="text-gray-500 text-sm">Кандидат не выбран</div>;
    }

    return (
        <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-3" data-testid="candidate-status-summary">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Текущий этап</div><div className="font-semibold">{statusStage(currentStatus, statuses)}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Состояние</div><div className="font-semibold">{statusState(currentStatus, candidate.stage, statuses)}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Ближайшее действие</div><div className="font-semibold">{statusNextAction(currentStatus, statuses)}</div></div>
            </div>
            <h3 className="text-sm font-semibold text-slate-700">Доступные переходы</h3>

            <CandidateStatusTree
                currentStatus={currentStatus ?? candidate?.status}
                statusesFromApi={statuses}
                disabled={loading || !candidate?.id}
                allowedTransitions={allowed}
                onSelect={(value) => candidate?.id && handleStatusChange(value)}
            />

            {pendingStatus && <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3">
                <label className="block text-sm font-semibold text-slate-800" htmlFor="candidate-status-date">Дата для статуса «{statusLabel(pendingStatus)}»</label>
                <input id="candidate-status-date" type="date" value={pendingDate} onChange={(event) => setPendingDate(event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2" />
                <div className="flex gap-2">
                    <button type="button" disabled={!pendingDate || loading} onClick={() => handleStatusChange(pendingStatus, pendingDate)} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Сохранить статус</button>
                    <button type="button" onClick={() => setPendingStatus(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Отмена</button>
                </div>
            </div>}

            {showOffer ? (
            <div className="flex gap-4">
                <button
                    type="button"
                    disabled={candidate?.offer_sent || loading}
                    onClick={() => setOfferOpen(true)}
                    className={`px-3 py-1 rounded text-sm ${
                        candidate?.offer_sent
                            ? "bg-green-200 text-gray-600"
                            : "bg-green-600 text-white"
                    }`}
                >
                    {candidate?.offer_sent ? "Оффер отправлен" : "Отправить оффер"}
                </button>
            </div>
            ) : null}

            <OfferModal
                open={offerOpen}
                onClose={() => setOfferOpen(false)}
                onSubmit={handleOfferSubmit}
                defaultText={buildOfferLetterText(candidate.full_name, `Здравствуйте, ${candidate.full_name || ""}!\n\nМы готовы сделать вам предложение о работе.`)}
                defaultPosition={candidate.last_vacancy_title || candidate.position || ""}
            />
        </div>
    );
}
